import { createContext, useContext } from "react"

import { VertesiaClient } from "@vertesia/client"
import { AuthTokenPayload, PrincipalType } from "@vertesia/common"
import { jwtDecode } from "jwt-decode"

import { CONFIGURATION } from "@env/variables"
import { getComposableToken } from "@/plugins/session/auth/composable"
import { firebaseAuth } from "@/plugins/session/auth/firebase"
import TypeRegistry from "@/plugins/session/TypeRegistry"

export const LastSelectedAccountId_KEY = "vertesia.nfwf.lastSelectedAccountId"
export const LastSelectedProjectId_KEY = "vertesia.nfwf.lastSelectedProjectId"

class UserSession {
    isLoading = true
    client: VertesiaClient
    authError?: Error
    authToken?: AuthTokenPayload
    typeRegistry?: TypeRegistry
    setSession?: (session: UserSession) => void
    lastSelectedAccount?: string | null
    lastSelectedProject?: string | null

    constructor(client?: VertesiaClient, setSession?: (session: UserSession) => void) {
        if (client) {
            this.client = client
        } else {
            this.client = new VertesiaClient({
                serverUrl: CONFIGURATION.endpoints.studio,
                storeUrl: CONFIGURATION.endpoints.zeno,
            })
        }

        if (setSession) {
            this.setSession = setSession
        }

        this.logout = this.logout.bind(this)
    }

    get store() {
        return this.client.store
    }

    get user() {
        return this.authToken
    }

    get account() {
        return this.authToken?.account
    }

    get project() {
        return this.authToken?.project
    }

    get accounts() {
        return this.authToken?.accounts
    }

    get authCallback() {
        return this.rawAuthToken.then(token => `Bearer ${token}`)
    }

    get isUser() {
        return this.authToken?.type === PrincipalType.User
    }

    get rawAuthToken() {
        return getComposableToken().then(res => {
            const token = res?.rawToken
            if (!token) {
                throw new Error("No token available")
            }
            this.authToken = jwtDecode(token)
            return token
        })
    }

    signOut() {
        this.logout()
    }

    getAccount() {
        return this.authToken?.account
    }

    async login(token: string) {
        this.authError = undefined
        this.isLoading = false
        this.client.withAuthCallback(() => this.authCallback)
        this.authToken = jwtDecode(token)

        localStorage.setItem(LastSelectedAccountId_KEY, this.authToken.account.id)
        localStorage.setItem(LastSelectedProjectId_KEY + "-" + this.authToken.account.id, this.authToken.project?.id ?? "")

        await this._loadTypes()

        return Promise.resolve()
    }

    isLoggedIn() {
        return !!this.authToken
    }

    logout() {
        if (this.authToken) {
            firebaseAuth.signOut()
        }
        this.authError = undefined
        this.isLoading = false
        this.authToken = undefined
        this.typeRegistry = undefined
        this.setSession = undefined
        this.client.withAuthCallback()
        localStorage.removeItem(LastSelectedAccountId_KEY)
    }

    async switchAccount(targetAccountId: string) {
        localStorage.setItem(LastSelectedAccountId_KEY, targetAccountId)
        window.location.replace(`${window.location.pathname}?a=${targetAccountId}`)
    }

    async switchProject(projectId: string) {
        const accountId = localStorage.getItem(LastSelectedAccountId_KEY)
        window.location.replace(`${window.location.pathname}?a=${accountId}&p=${projectId}`)
    }

    async _loadTypes() {
        if (this.project) {
            return this.store.types.list({}, { layout: true }).then(types => this.typeRegistry = new TypeRegistry(types)).catch((err) => {
                throw err
            })
        }
    }

    async reloadTypes() {
        return this._loadTypes().then(() => {
            this.setSession?.(this.clone())
        })
    }

    clone() {
        const session = new UserSession(this.client)
        session.isLoading = this.isLoading
        session.authError = this.authError
        session.authToken = this.authToken
        session.setSession = this.setSession
        session.lastSelectedAccount = this.lastSelectedAccount
        session.switchAccount = this.switchAccount
        session.typeRegistry = this.typeRegistry
        return session
    }
}

const UserSessionContext = createContext<UserSession | undefined>(undefined)

export function useUserSession() {
    const session = useContext(UserSessionContext)

    if (!session) {
        throw new Error("useUserSession must be used within a UserSessionProvider")
    }
    return session
}

export { UserSession, UserSessionContext }